import { useRouter } from 'next/router'
import Image from 'next/image'
import { useTranslation } from 'react-i18next'
import Seo from '@components/seo/seo'
import BannerComponent from '@components/banner'
import BlockContent from '@components/manufacturer/block-content'
import ProductCardNew from '@components/product/product-cards/product-card-new'
import Container from '@components/ui/container'
import Button from '@components/ui/button'
import Link from '@components/ui/link'
import PromotionComponent from '@components/promotionBanner'

interface CmsPageProps {
  pageTitle: string
  urlKey: string
  contentTitle: string
  contentDescription: string
  banners: any[]
  products: any[]
  promotions: any[]
  promotionTitle: string
  seo: {
    meta_title: string
    meta_keywords: string
    meta_description: string
  } | null
}

export default function CmsPage({
  pageTitle,
  urlKey,
  contentTitle,
  contentDescription,
  banners,
  products,
  promotions,
  promotionTitle,
  seo,
}: CmsPageProps) {
  const router = useRouter()
  const { t: translate } = useTranslation('promise')
  return (
    <>
      <Seo
        title={seo?.meta_title || pageTitle}
        description={seo?.meta_description}
        path={`/cms-pages/${urlKey}`}
      />

      <Container className="border-border-base checkout mx-auto max-w-screen-xl my-10 px-2 sm:px-0">
        {banners.map(
          (banner) =>
            banner?.image && (
              <BannerComponent key={banner.banner_id} banner={banner} />
            ),
        )}

        <div className="w-full my-5 sm:my10">
          <h1
            className="text-[28px] 2xl:text-[46px] font-bold text-grey-960 mb-2 capitalize text-center"
            data-testid={`${urlKey}-text-brand-generic-title`}
          >
            {contentTitle}
          </h1>

          <span
            className="text-base generic_content lg:text-xl font-normal leading-7"
            data-testid={`${urlKey}-text-brand-generic-description`}
            dangerouslySetInnerHTML={{
              __html: contentDescription,
            }}
          />
        </div>

        {/* Render Product Catalogs */}
        {products.length > 0 && (
          <section className="my-4 sm:my-8">
            {products.map((catalog) => (
              <div key={catalog.catalog_id}>
                <h3 className="text-28px leading-9 font-bold text-brand-dark font-inter my-8">
                  {catalog.catalog_title}
                </h3>
                {catalog?.link_position === 'top' && catalog?.link && (
                  <div className="text-center my-5">
                    <Link href={catalog?.link} target="_blank">
                      <button
                        className={`min-w-[126px] md:min-w-[207px] px-3 md:px-6 lg:px-12 py-4 lg:py-3 my-4  hover:bg-brand  !mx-4 text-[15px] md:text-base rounded-md !font-normal font-poppins leading-4 bg-brand-darkblue text-white shadow-sm transition-shadow duration-200`}
                      >
                        {catalog?.link_label}
                      </button>
                    </Link>
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 xl:grid-cols-5 gap-2 md:gap-4 mt-2">
                  {catalog['product-info']?.map((product, idx) => (
                    <div
                      key={`${idx}-${idx}`}
                      className="relative borobazar-alpine-product-card rounded-[10px] bg-white transition-all hover:shadow-product-hover shadow-bestSeller"
                    >
                      <ProductCardNew
                        product={product}
                        page={urlKey}
                        uniqueKey={`${idx}[${idx + 1}]`}
                        allowModal={true}
                      />
                    </div>
                  ))}
                </div>

                {catalog?.link_position === 'bottom' && catalog?.link && (
                  <div className="text-center my-5">
                    <Link href={catalog?.link} target="_blank">
                      <button
                        className={`min-w-[126px] md:min-w-[207px] px-3 md:px-6 lg:px-12 py-4 lg:py-3 my-4  hover:bg-brand  !mx-4 text-[15px] md:text-base rounded-md !font-normal font-poppins leading-4 bg-brand-darkblue text-white shadow-sm transition-shadow duration-200`}
                      >
                        {catalog?.link_label}
                      </button>
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </section>
        )}

        {promotions.length > 0 && (
          <section className="mt-5 sm:my-8">
            <h3 className="text-sm xl:text-28px leading-9 font-bold text-brand-dark font-inter my-8">
              {promotionTitle}
            </h3>
            <div className="grid grid-cols-1 ">
              {promotions?.length > 0 && (
                <section>
                  <div className="grid grid-cols-1 gap-5">
                    {promotions &&
                      promotions?.length > 0 &&
                      promotions.map((promotionItem, index) => (
                        <>
                          <PromotionComponent
                            promotion={promotionItem}
                            key={index}
                          />
                        </>
                      ))}
                  </div>
                </section>
              )}
            </div>
          </section>
        )}

        <div className="text-28px xl:text-xl leading-9 text-center font-bold text-brand-darkblue font-inter   mb-6">
          {' THE FOOD SERVICE DIRECT PROMISE'}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {/* Quality Shipments Card */}
          <div className="flex flex-col items-center p-6 border rounded-lg shadow-md">
            <div className="w-32 h-32 flex items-center justify-center border rounded-full mb-4  shadow-ourPromise p-2">
              <div className="w-full h-full flex items-center justify-center  bg-brand rounded-full">
                <Image
                  src={'/assets/images/quality_shipment.svg'}
                  alt="Quality Shipments"
                  width={60}
                  height={60}
                />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-brand-darkblue mb-2">
              {translate('quality_shipments_title')}
            </h3>
            <p
              className="text-center text-gray-600 mb-4 text-sm"
              dangerouslySetInnerHTML={{
                __html: translate('quality_shipments_description'),
              }}
            />
            <Link
              href="https://foodservicedirect.zendesk.com/hc/en-us/sections/360001987873-Shipping"
              target="_blank"
              className="w-full flex-col mt-auto"
            >
              <button className=" rounded bg-brand-darkblue text-white px-4 py-2 w-full flex-col mt-auto">
                {translate('quality_shipments_button')}
              </button>
            </Link>
          </div>

          {/* On-Demand Sourcing Card */}
          <div className="flex flex-col items-center p-6 border rounded-lg shadow-md">
            <div className="w-32 h-32 flex items-center justify-center border rounded-full mb-4  shadow-ourPromise p-2">
              <div className="w-full h-full flex items-center justify-center  bg-brand rounded-full">
                <Image
                  src={'/assets/images/ondemand_sourcing.svg'}
                  alt="On Demand Sourcing"
                  width={60}
                  height={60}
                />
              </div>
            </div>

            <h3 className="text-lg font-semibold text-brand-darkblue mb-2">
              {translate('on_demand_sourcing_title')}
            </h3>
            <p
              className="text-center text-gray-600 mb-4 text-sm"
              dangerouslySetInnerHTML={{
                __html: translate('on_demand_sourcing_description'),
              }}
            />
            <Link
              href="https://foodservicedirect.zendesk.com/hc/en-us/articles/360021259613"
              target="_blank"
              className="w-full flex-col mt-auto"
            >
              <button className=" rounded bg-brand-darkblue text-white px-4 py-2 w-full ">
                {translate('on_demand_sourcing_button')}
              </button>
            </Link>
          </div>

          {/* Service and Support Card */}
          <div className="flex flex-col items-center p-6 border rounded-lg shadow-md">
            <div className="w-32 h-32 flex items-center justify-center border rounded-full mb-4  shadow-ourPromise p-2">
              <div className="w-full h-full flex items-center justify-center  bg-brand rounded-full">
                <Image
                  src={'/assets/images/service_support.svg'}
                  alt="Service and support"
                  width={60}
                  height={60}
                />
              </div>
            </div>

            <h3 className="text-lg font-semibold text-brand-darkblue mb-2">
              {translate('service_support_title')}
            </h3>
            <p
              className="text-center text-gray-600 mb-4 text-sm"
              dangerouslySetInnerHTML={{
                __html: translate('service_support_description'),
              }}
            />
            <Link
              href="https://foodservicedirect.zendesk.com/hc/en-us/articles/360008319853-How-can-I-get-in-touch-"
              target="_blank"
              className="w-full flex-col mt-auto"
            >
              <button className=" rounded bg-brand-darkblue text-white px-4 py-2 w-full flex-col mt-auto">
                {translate('service_support_button')}
              </button>
            </Link>
          </div>

          {/* Shipping Savings Card */}
          <div className="flex flex-col items-center p-6 border rounded-lg shadow-md">
            <div className="w-32 h-32 flex items-center justify-center border rounded-full mb-4  shadow-ourPromise p-2">
              <div className="w-full h-full flex items-center justify-center  bg-brand rounded-full">
                <Image
                  src={'/assets/images/shipping_service.svg'}
                  alt="Shipping Service"
                  width={60}
                  height={60}
                />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-brand-darkblue mb-2">
              {translate('shipping_savings_title')}
            </h3>
            <p
              className="text-center text-gray-600 mb-4 text-sm"
              dangerouslySetInnerHTML={{
                __html: translate('shipping_savings_description'),
              }}
            />
            <Link
              href="https://foodservicedirect.zendesk.com/hc/en-us/sections/360001987873-Shipping"
              target="_blank"
              className="w-full flex-col mt-auto"
            >
              <button className=" rounded bg-brand-darkblue text-white px-4 py-2 w-full flex-col mt-auto">
                {translate('shipping_savings_button')}
              </button>
            </Link>
          </div>
        </div>
      </Container>
    </>
  )
}
